import { DateTime } from "luxon";

import { VehicleProductionLine } from "../models/ProductionPlanner";
import { VehicleComplexity } from "../models/Vehicle";

export const complexityLabel = (c: VehicleComplexity): string => {
  switch (c) {
    case VehicleComplexity.Easy:
      return "Easy";
    case VehicleComplexity.Medium:
      return "Medium";
    case VehicleComplexity.Hard:
      return "Hard";
    default:
      return "Unranked";
  }
};

export const complexityShort = (c: VehicleComplexity): string => {
  switch (c) {
    case VehicleComplexity.Easy:
      return "E";
    case VehicleComplexity.Medium:
      return "M";
    case VehicleComplexity.Hard:
      return "H";
    default:
      return "?";
  }
};

export const complexityChipClasses = (c: VehicleComplexity): string => {
  switch (c) {
    case VehicleComplexity.Easy:
      return "bg-green-100 text-green-800";
    case VehicleComplexity.Medium:
      return "bg-amber-100 text-amber-800";
    case VehicleComplexity.Hard:
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

export const complexityBorderClasses = (c: VehicleComplexity): string => {
  switch (c) {
    case VehicleComplexity.Easy:
      return "border-l-4 border-l-green-500";
    case VehicleComplexity.Medium:
      return "border-l-4 border-l-amber-500";
    case VehicleComplexity.Hard:
      return "border-l-4 border-l-red-500";
    default:
      return "border-l-4 border-l-gray-300";
  }
};

export const lineLabel = (line: VehicleProductionLine): string =>
  line === VehicleProductionLine.OffLine ? "Off Line" : "Main Line";

export const lineChipClasses = (line: VehicleProductionLine): string =>
  line === VehicleProductionLine.OffLine
    ? "bg-red-50 text-red-700 border border-red-200"
    : "bg-blue-50 text-blue-700 border border-blue-200";

export const daysOnSite = (arrival: string | null, collected: string | null): number | null => {
  const iso = arrival ?? collected;
  if (!iso) return null;
  const diff = DateTime.utc().diff(DateTime.fromISO(iso, { zone: "utc" }), "days").days;
  return Math.max(0, Math.floor(diff));
};

export const ageBorderClasses = (days: number | null): string => {
  if (days === null) return "border-l-4 border-l-gray-200";
  if (days < 7) return "border-l-4 border-l-emerald-500";
  if (days < 21) return "border-l-4 border-l-amber-500";
  return "border-l-4 border-l-red-600";
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// True when this vehicle is a row in Wilma rather than UKVD details we looked up and didn't keep.
// A `mode=DataOnly` lookup (see `vrmLookupData`) comes back with no id, because nothing was
// written — so anything keyed on `vehicle.id` has to check this first.
export const isStoredVehicle = (vehicle: { id?: string | null } | null | undefined): boolean => {
  const id = vehicle?.id?.trim();
  return !!id && id.toLowerCase() !== EMPTY_GUID;
};
